import React, { useRef, useCallback } from "react";
import { TextField, InputAdornment, IconButton, Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { InsertEmoticon, Mic, Stop, Send, AutoAwesome } from "@mui/icons-material";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { useTranslation } from "react-i18next";
import ComposeAttachMenu from "./ComposeAttachMenu";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";
import RecordingIndicator from "./RecordingIndicator";
import AudioPreview from "./AudioPreview";
import { useComposeSelectionFormat } from "../../hooks/useComposeSelectionFormat";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";

const MessageInputField = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  isPersonalWhatsappThread = false,
  formatOptions,
  isRecording,
  recordingDuration = 0,
  startRecording,
  stopRecording,
  discardRecording = () => {},
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  files = [],
  audioURL,
  onRemoveAudio,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  sendBlockedMissingPhone = false,
  onOpenProductPicker,
}) => {
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { t } = useTranslation("chat");
  const inputBorderRadius = 16;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const isInternalNote =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote,
    isPersonalWhatsapp: isPersonalWhatsappThread,
  });
  /** Mostrar botão de IA (sempre visível quando há handler; desabilitado sem histórico). */
  const canUseAiSuggestion = !!onGenerateSuggestion && channelType !== "Xpro";
  const aiDisabled = isGeneratingSuggestion || !hasHistory;

  const { composeInputHandlers, selectionToolbar } = useComposeSelectionFormat({
    inputRef,
    setMessage,
    channelType,
    formatOptions,
  });

  const handleOpenProductPickerFromMenu = useCallback(() => {
    onOpenProductPicker?.();
    queueMicrotask(() => inputRef.current?.focus());
  }, [onOpenProductPicker]);

  const textField = (
    <TextField
      fullWidth
      multiline
      minRows={1}
      maxRows={4}
      variant="outlined"
      placeholder={
        selectedChannel === INTERNAL_NOTE_CHANNEL_ID || channelType === "Xpro"
          ? t("input.placeholderInternal")
          : isPersonalWhatsappThread
          ? t("input.placeholderPersonalWhatsapp")
          : channelType
          ? t("input.placeholderChannel", { channel: channelType })
          : t("input.placeholderGeneric")
      }
      value={message}
      onChange={(e) => setMessage(e.target.value)}
      inputProps={composeInputHandlers}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (sendBlockedMissingPhone) return;
          handleSendMessage();
        }
      }}
      InputProps={{
        style: {
          // Dentro do "capsule" integrado, o TextField não deve ter borda própria.
          borderRadius: 0,
          fontSize: isMobile ? "0.95rem" : "1rem",
        },
        inputRef: inputRef,
        startAdornment: (
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <IconButton
              onClick={handleEmojiPickerOpen}
              edge="start"
              size={isMobile ? "small" : "medium"}
              aria-label={t("input.emojiAria")}
            >
              <InsertEmoticon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{ mr: 0.25, flexWrap: "nowrap", display: "flex", alignItems: "center", gap: 0 }}
          >
            {canUseAiSuggestion ? (
              <Tooltip
                title={
                  aiDisabled
                    ? isGeneratingSuggestion
                      ? t("input.aiGenerating")
                      : t("input.aiNoHistory")
                    : t("input.aiSuggest")
                }
              >
                <span>
                  <IconButton
                    onClick={onGenerateSuggestion}
                    disabled={aiDisabled}
                    size={isMobile ? "small" : "medium"}
                    aria-label={t("input.aiSuggest")}
                  >
                    <AutoAwesome fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <ComposeAttachMenu
              openFilePicker={openFilePicker}
              showAttachProducts={typeof onOpenProductPicker === "function"}
              onOpenProductPicker={handleOpenProductPickerFromMenu}
              showLocation={false}
              size={isMobile ? "small" : "medium"}
            />
            {/* Mic/send principal fica no botão externo do composer (MessageInput) */}
          </InputAdornment>
        ),
      }}
      sx={{
        // A cápsula externa já desenha a borda e arredondamento.
        "& .MuiInputBase-root": {
          minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
          boxSizing: "border-box",
          borderRadius: "0 !important",
          overflow: "hidden",
          clipPath: "none",
          "&::-webkit-scrollbar": { width: "5px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#b0b0b0", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#888888" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "0 !important",
          overflow: "hidden",
          clipPath: "none",
          "& fieldset": { border: "none" },
          "&:hover fieldset": { border: "none" },
          "&.Mui-focused fieldset": { border: "none" },
        },
        "& .MuiOutlinedInput-notchedOutline": { border: "none !important", borderRadius: "0 !important" },
        "& textarea, & .MuiInputBase-inputMultiline": {
          overflowY: "auto",
          resize: "none",
          boxSizing: "border-box",
          borderRadius: "inherit",
          overflowX: "hidden",
          padding: "4px 10px",
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#b0b0b0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#888888",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
          },
        },
      }}
    />
  );

  const textOrVoiceSlot = isRecording ? (
    <RecordingIndicator
      embedded
      durationSeconds={recordingDuration}
      onStop={stopRecording}
      onDiscard={discardRecording}
    />
  ) : audioURL ? (
    <AudioPreview embedded audioURL={audioURL} onRemove={onRemoveAudio} />
  ) : (
    textField
  );

  const integratedComposer = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "stretch",
        borderRadius: `${inputBorderRadius}px`,
        minHeight: COMPOSER_ROW_HEIGHT_PX,
        overflow: "hidden",
        ...composerSurface.shell,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          alignSelf: "stretch",
          px: 0.25,
          ...composerSurface.divider,
        }}
      >
        <ChannelSelector integrated selectedChannel={selectedChannel} setSelectedChannel={setSelectedChannel} />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {textOrVoiceSlot}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 0.5 : 1,
      }}
    >
      {isMobile ? (
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, width: "100%" }}>
          {integratedComposer}
          <IconButton
            size="small"
            onClick={isRecording ? stopRecording : startRecording}
            aria-label={t("input.recordVoiceAria")}
            sx={{ flexShrink: 0 }}
          >
            {isRecording ? <Stop fontSize="small" /> : <Mic fontSize="small" />}
          </IconButton>
          <IconButton
            onClick={handleSendMessage}
            disabled={sendBlockedMissingPhone || !composerHasSendPayload(message, file, files, audioURL)}
            aria-label={t("input.sendAria")}
            sx={{
              backgroundColor: isPersonalWhatsappThread ? "#128C7E" : "primary.main",
              color: "primary.contrastText",
              borderRadius: "50%",
              p: 1,
              flexShrink: 0,
              "&:hover": { backgroundColor: isPersonalWhatsappThread ? "#0f6f64" : "primary.dark" },
            }}
          >
            <Send fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        integratedComposer
      )}
      {selectionToolbar}
    </Box>
  );
};

export default MessageInputField;
